import { useCallback, useEffect, useRef, useState } from 'react';
import { css } from '@linaria/core';
import clsx from 'clsx';
import { Icon } from '@cbhq/cds-web/icons/Icon';
import { Box, HStack, VStack } from '@cbhq/cds-web/layout';
import { Pressable } from '@cbhq/cds-web/system/Pressable';
import { TextBody, TextHeadline, TextTitle1 } from '@cbhq/cds-web/typography';
import {
  ActionType,
  AnalyticsEventImportance,
  ComponentType,
  logEvent,
} from '@cbhq/client-analytics';

import { lockedColor, unlockedColor } from ':cloud/widgets/buildQuest/assets/colors';

export type CarouselItem = {
  id: number;
  name: string;
  imageUrl: string;
  isLocked: boolean;
  requiredLevel?: number;
};

interface CarouselProps {
  items: CarouselItem[];
}

const ITEM_WIDTH = 244;
const SCALE = 1.2;
const GAP = 24;

const hStackStyle = css`
  transition: transform 0.5s cubic-bezier(0.6, 0, 0.15, 1);
`;

const noTransition = css`
  transition: none !important;
`;

const imgStyle = css`
  max-height: 280px;
  object-fit: contain;
`;

const currentItemStyle = css`
  cursor: pointer;
  transform: scale(${SCALE});
  transition:
    opacity 0.3s,
    transform 0.3s;
  margin-left: ${GAP}px;
  margin-right: ${GAP}px;
`;

const otherItemStyle = css`
  cursor: pointer;
  transform: scale(1);
  transition:
    opacity 0.3s,
    transform 0.3s;
`;

export function Carousel({ items }: CarouselProps): React.ReactElement {
  const [currentItem, setCurrentItem] = useState<CarouselItem>(items[0]);
  const [isInitialMount, setIsInitialMount] = useState<boolean>(true);
  const containerRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<Record<number, HTMLDivElement | null>>({});

  const handleClick = (item: CarouselItem) => {
    setCurrentItem(item);
  };

  useEffect(() => {
    if (containerRef.current && currentItem) {
      // Get the container width and the selected item left position
      const containerWidth = containerRef.current.offsetWidth;
      const selectedItemElement = itemRefs.current[currentItem.id];
      const itemLeftPosition = selectedItemElement?.offsetLeft || 0;
      // Center the selected item
      const centerOffset = containerWidth / 2 - (ITEM_WIDTH * SCALE) / 2 + GAP * SCALE;
      const translateXValue = centerOffset - itemLeftPosition;
      // Move the container to center the selected item
      containerRef.current.style.transform = `translateX(${translateXValue}px)`;

      // fix to center the selected item on initial mount
      if (isInitialMount) {
        setIsInitialMount(false);
      }
    }
  }, [currentItem, isInitialMount]);

  const handlePress = useCallback(() => {
    if (currentItem.isLocked) {
      logEvent(
        'quest_prize_locked_click',
        {
          action: ActionType.click,
          componentType: ComponentType.button,
        },
        AnalyticsEventImportance.high,
      );
    } else {
      logEvent(
        'quest_prize_claim_click',
        {
          action: ActionType.click,
          componentType: ComponentType.button,
        },
        AnalyticsEventImportance.high,
      );
    }
  }, [currentItem.isLocked]);

  return (
    <VStack width="100%" position="relative" gap={8}>
      <HStack
        gap={5}
        ref={containerRef}
        justifyContent="center"
        className={clsx(hStackStyle, { [noTransition]: isInitialMount })}
        key="carousel-container"
      >
        {items.map((item) => {
          const isCurrent = item.id === currentItem.id;
          return (
            <Box
              key={item.id}
              ref={(el) => {
                itemRefs.current[item.id] = el as HTMLDivElement;
              }}
              onClick={() => handleClick(item)}
              height={330}
              background="backgroundInverse"
              borderRadius="roundedLarge"
              justifyContent="center"
              alignItems="flex-end"
              opacity={isCurrent ? 1 : 0.6}
              width={ITEM_WIDTH}
              flexShrink={0}
              position="relative"
              className={clsx(isCurrent ? currentItemStyle : otherItemStyle, {
                [noTransition]: isInitialMount,
              })}
            >
              {isCurrent && (
                <Box
                  position="absolute"
                  top={8}
                  left={0}
                  right={0}
                  zIndex={999}
                  width="100%"
                  justifyContent="center"
                >
                  <TextBody as="p" color="currentColor">
                    {item.name}
                  </TextBody>
                </Box>
              )}
              <img src={item.imageUrl} alt={item.name} width="100%" className={imgStyle} />
            </Box>
          );
        })}
      </HStack>
      <VStack alignItems="center" gap={4} spacingBottom={4}>
        <VStack alignItems="center" gap={1}>
          <HStack alignItems="center" gap={1}>
            <Icon
              name={currentItem.isLocked ? 'lock' : 'shield'}
              size="m"
              dangerouslySetColor={currentItem.isLocked ? lockedColor : unlockedColor}
            />
            <TextTitle1
              as="p"
              dangerouslySetColor={currentItem.isLocked ? lockedColor : unlockedColor}
            >
              {currentItem.isLocked ? 'Locked' : 'Unlocked'}
            </TextTitle1>
          </HStack>
          {currentItem.isLocked && (
            <HStack alignItems="center">
              <TextBody as="p" color="foregroundMuted">
                Get to level {currentItem.requiredLevel} to unlock
              </TextBody>
            </HStack>
          )}
        </VStack>
        <Pressable
          as="button"
          background="backgroundInverse"
          borderRadius="roundedFull"
          onPress={handlePress}
        >
          <Box spacingHorizontal={4} spacingVertical={2}>
            <TextHeadline as="p" dangerouslySetColor="var(--background)">
              {currentItem.isLocked ? 'Get more exp' : 'Claim now'}
            </TextHeadline>
          </Box>
        </Pressable>
      </VStack>
    </VStack>
  );
}
